// The person's own credentials on Your account (#807): the workspace's API keys, their
// sessions, and their password.
//
// Read off `control_plane/routes/auth.py`; this slice adds no route and changes none.
// `POST /auth/api-keys` and `…/rotate` answer the key itself in `api_key`, once: the
// row the list reads back never carries it. `POST /auth/password` revokes every
// session of the user, this one included, so a caller treats its success as signed out.

import { ApiError, type Api } from "./http";

export const API_KEYS_PATH = "/auth/api-keys";
export const SESSIONS_PATH = "/auth/sessions";
export const REVOKE_OTHER_SESSIONS_PATH = "/auth/sessions/revoke-others";
export const CHANGE_PASSWORD_PATH = "/auth/password";

/** `api_keys.public_row`: everything but the hash. */
export interface ApiKeyRow {
  api_key_id: string;
  name: string;
  key_prefix: string;
  scope_preset: string;
  permissions: string[];
  status: "active" | "revoked";
  created_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
}

/** A create or a rotate: the new row, and the key, which is never shown again. */
export type MintedApiKey = ApiKeyRow & { api_key: string };

/** The presets a key may be made with here; `custom` needs a permission list this screen does not offer. */
export type ScopePreset = "read_only" | "full_access";

/** `Session.public()`: one of the user's live sessions. */
export interface SessionRow {
  session_id: string;
  user_agent: string;
  ip_address: string;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
}

export interface ApiKeyActions {
  list(): Promise<ApiKeyRow[]>;
  create(name: string, scope: ScopePreset): Promise<MintedApiKey>;
  revoke(apiKeyId: string): Promise<ApiKeyRow>;
  rotate(apiKeyId: string): Promise<MintedApiKey>;
}

export interface SessionActions {
  list(): Promise<SessionRow[]>;
  /** How many sessions were signed out. */
  revokeOthers(): Promise<number>;
}

const keyPath = (apiKeyId: string, act: "revoke" | "rotate") =>
  `${API_KEYS_PATH}/${encodeURIComponent(apiKeyId)}/${act}`;

export const apiKeyActions = (api: Api): ApiKeyActions => ({
  list: () => api.get<ApiKeyRow[]>(API_KEYS_PATH),
  create: (name, scope) => api.post<MintedApiKey>(API_KEYS_PATH, { name, scope_preset: scope }),
  revoke: (apiKeyId) => api.post<ApiKeyRow>(keyPath(apiKeyId, "revoke")),
  rotate: (apiKeyId) => api.post<MintedApiKey>(keyPath(apiKeyId, "rotate")),
});

export const sessionActions = (api: Api): SessionActions => ({
  list: () => api.get<SessionRow[]>(SESSIONS_PATH),
  revokeOthers: async () =>
    (await api.post<{ revoked: number }>(REVOKE_OTHER_SESSIONS_PATH)).revoked,
});

/** Change the password. Every session ends, this one too; the answer counts them. */
export const changePassword = async (
  api: Api,
  currentPassword: string,
  newPassword: string,
): Promise<number> =>
  (
    await api.post<{ revoked_other_sessions: number }>(CHANGE_PASSWORD_PATH, {
      current_password: currentPassword,
      new_password: newPassword,
    })
  ).revoked_other_sessions;

// ------------------------------------------------- deleting the account --
//
// Read off `control_plane/routes/auth.py` (#1133). A request is not a deletion: the
// account is deleted at `deletes_at`, a date the server alone states, and a sign-in
// before then reaches these three routes and nothing else.

export const DELETE_ACCOUNT_PATH = "/auth/delete";
export const DELETION_PATH = "/auth/deletion";
export const CANCEL_DELETION_PATH = "/auth/deletion/cancel";
/** `auth/service.py::ACCOUNT_DELETE_PURPOSE`: the step-up a request rides. */
export const ACCOUNT_DELETE_PURPOSE = "account_delete";

/**
 * When a deletion stamp falls for the reader: the day, the time and the zone, in the
 * reader's own zone (#1172). A bare UTC day is a local day late west of UTC. A stamp that
 * is not a date is shown as the server gave it.
 */
export const deletionTimeOf = (stamp: string): string => {
  const at = new Date(stamp);
  if (Number.isNaN(at.getTime())) return stamp;
  return at.toLocaleString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
};

/** `GET /auth/deletion`, and what a cancel answers: the dates are null with no request. */
export interface DeletionState {
  status: "requested" | "none";
  requested_at: string | null;
  deletes_at: string | null;
  /** The date a request made now would get; answered by the read, and only with no request. */
  would_delete_at?: string | null;
}

/** When a request made now would delete the account, as the server says; null if it says none. */
export const readWouldDeleteAt = async (api: Api): Promise<string | null> => {
  const state = await api.get<DeletionState>(DELETION_PATH);
  return typeof state?.would_delete_at === "string" ? state.would_delete_at : null;
};

/** Ask for the account to be deleted. The session ends; the answer is when it happens. */
export const requestDeletion = async (api: Api, grant: string): Promise<string | null> => {
  const recorded = await api.post<{ deletes_at?: unknown }>(DELETE_ACCOUNT_PATH, {
    step_up_grant: grant,
  });
  return typeof recorded?.deletes_at === "string" ? recorded.deletes_at : null;
};

export const cancelDeletion = (api: Api): Promise<DeletionState> =>
  api.post<DeletionState>(CANCEL_DELETION_PATH);

const refusalOf = (failure: unknown, status: number): Record<string, unknown> | null => {
  if (!(failure instanceof ApiError) || failure.status !== status) return null;
  const detail = (failure.body as { detail?: unknown } | null)?.detail;
  return typeof detail === "object" && detail !== null ? (detail as Record<string, unknown>) : null;
};

/**
 * What a refusal says about a session whose account is being deleted: the 403 every
 * route but the three above answers it carries `deletes_at`, which is null for an
 * account whose request has no stamp yet. Null when the refusal is any other.
 */
export function deletionPendingOf(failure: unknown): { deletesAt: string | null } | null {
  const detail = refusalOf(failure, 403);
  if (detail === null || !("deletes_at" in detail)) return null;
  return { deletesAt: typeof detail.deletes_at === "string" ? detail.deletes_at : null };
}

/**
 * The workspaces and organisations a request would leave without an owner: the 409
 * `last_owner` names them. Null when the refusal is any other.
 */
export function lastOwnerOf(failure: unknown): string[] | null {
  const detail = refusalOf(failure, 409);
  if (detail === null || detail.error !== "last_owner") return null;
  const names = detail.organisations;
  return Array.isArray(names) ? names.filter((name): name is string => typeof name === "string") : [];
}
